<template>
  <button
    class="dca-cell text-left"
    :class="{
      'dca-cell--selected': selected,
      'dca-cell--empty': isEmpty,
    }"
    type="button"
    :aria-pressed="selected"
    :data-testid="`dca-cell-${rowId}-${placementNumber}`"
    @click="$emit('select')"
  >
    <SoSkeleton v-if="loading" height="12" data-testid="dca-cell-skeleton" />

    <!--
      An empty step is configurable, not merely unconfigured - so it reads as an
      invitation rather than a statement. Nothing in the data model prevents any
      cell being filled, including Occupier beyond its 1st placement.
    -->
    <template v-else-if="isEmpty">
      <span class="dca-cell__add text-caption" aria-hidden="true">+</span>
      <span class="dca-cell__empty text-caption text-medium-emphasis">
        Add a split
      </span>
    </template>

    <template v-else>
      <span class="dca-cell__bar" data-testid="dca-cell-bar">
        <span
          v-for="allocation in allocations"
          :key="allocation.dca"
          class="dca-cell__bar-part"
          :style="{
            width: `${allocation.percentage}%`,
            backgroundColor: colourFor(allocation.dca),
          }"
        />
      </span>
      <span class="dca-cell__split text-caption">{{ split }}</span>
    </template>
  </button>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import SoSkeleton from "@/components/SoSkeleton.vue"
  import { IDcaAllocation } from "@/models/dcaAllocation/dcaAllocation.types"
  import { agencyColour, agencyName } from "../dcaAgencyColours"

  export default defineComponent({
    components: { SoSkeleton },
    props: {
      rowId: { type: String, required: true },
      placementNumber: { type: Number, required: true },
      allocations: {
        type: Array as PropType<IDcaAllocation[]>,
        default: () => [],
      },
      selected: { type: Boolean, default: false },
      loading: { type: Boolean, default: false },
    },
    emits: ["select"],
    computed: {
      isEmpty(): boolean {
        return this.allocations.length === 0
      },
      /** The split itself, e.g. "Coeo 80% · Pastdue 20%". */
      split(): string {
        return this.allocations
          .map(
            (allocation) =>
              `${agencyName(allocation.dca)} ${allocation.percentage}%`,
          )
          .join(" · ")
      },
    },
    methods: {
      colourFor(dca: string): string {
        return agencyColour(dca)
      },
    },
  })
</script>

<style scoped>
  .dca-cell {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 6px;
    width: 100%;
    min-height: 96px;
    padding: 8px 10px;
    background: rgb(var(--v-theme-surface));
    border: 1px solid rgb(var(--v-theme-border));
    border-radius: var(--theme-radius);
    cursor: pointer;
  }

  .dca-cell--empty {
    align-items: center;
    gap: 2px;
    background: transparent;
    border-style: dashed;
  }

  .dca-cell--empty:hover {
    border-color: rgb(var(--v-theme-primary));
    border-style: solid;
  }

  .dca-cell__add {
    font-size: 20px;
    line-height: 1;
    color: rgb(var(--v-theme-primary));
  }

  /*
   * Keyboard focus, distinct from selection. A cell is a button with aria-pressed, so it
   * can be tabbed to without being selected - and without this it looked identical to an
   * unfocused one, leaving a keyboard user with no idea where they were across twelve
   * cells. Selection keeps its own treatment below, the way SoRadioCard separates the two.
   */
  .dca-cell:focus-visible {
    outline: var(--theme-outline);
  }

  .dca-cell--selected {
    border-color: rgb(var(--v-theme-primary));
    box-shadow: 0 0 0 2px rgba(var(--v-theme-primary), 0.2);
  }

  .dca-cell__bar {
    display: flex;
    height: 11px;
    overflow: hidden;
    border-radius: 2px;
  }

  .dca-cell__bar-part {
    display: block;
    height: 100%;
    /*
     * Two agency brand colours sit at the extremes of the lightness range - Coeo's
     * near-fluorescent green against a white card, First Locate's near-black navy
     * against the dark one - and at those ends the fill stops separating from the
     * card behind it. A hairline in the surface's own foreground colour gives every
     * segment an edge in both themes, without altering anyone's brand colour.
     */
    box-shadow: inset 0 0 0 1px rgba(var(--v-theme-on-surface), 0.25);
  }

  .dca-cell__empty,
  .dca-cell__split {
    display: block;
  }
</style>
